'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { Address } from 'viem';
import { Button, Card, Skeleton, Badge, Select, SelectItem } from '@heroui/react';
import Link from 'next/link';
import Image from 'next/image';
import { useDirectListings } from '@/hooks/marketplace/useDirectListings';
import { useEnglishAuctions } from '@/hooks/marketplace/useEnglishAuctions';
import { MARKETPLACE_CONTRACTS } from '@/constants/marketplace/contracts';
import { getMarketplaceCollections } from '@/constants/marketplace/collections';
import type { NFTCollection } from '@/constants/marketplace/collections';
import { chainSettings, type ChainType } from '@/constants/chain-specific';
import { Format } from '@/components/Format';

// Helper function to generate canonical collection URL
function getCanonicalCollectionUrl(chainId: number, collection: NFTCollection): string {
  // Use slug if available, otherwise fallback to address
  if (collection.slug) {
    return `/marketplace/collection/${chainSettings[Number(chainId) as ChainType].chainSlug}/${collection.slug}`;
  }
  return `/marketplace/collection/${chainSettings[Number(chainId) as ChainType].chainSlug}/${collection.address}`;
}

// Simple Icon Components
const SortAlphaIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 17 2.5-5 2.5 5" /><path d="M10 7h2a2 2 0 1 1 0 4h-2" /><path d="M2 7h3" /><path d="M16 7h6" /><path d="M19 4v6" /><path d="M16 17h6" /><path d="M19 20v-6" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const ListingsIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M7 7h10" /><path d="M7 12h10" /><path d="M7 17h10" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const AuctionsIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" /><path d="M12 6v6l4 2" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const TotalActivityIcon = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v5" /><path d="m4.93 10.93 3.54 3.54" /><path d="M2 18h5" /><path d="M19.07 10.93l-3.54 3.54" /><path d="M22 18h-5" /><path d="m12 12 4 10" /><path d="m12 12-4 10" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface CollectionsProps {
  chainId: number;
}
type CollectionWithActivity = NFTCollection & {
  listingsCount: number;
  auctionsCount: number;
};
type SortOption = 'alphabetical' | 'listings' | 'auctions' | 'total';
export default function Collections({
  chainId
}: CollectionsProps) {
  const [collections, setCollections] = useState<CollectionWithActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortOption, setSortOption] = useState<SortOption>('listings');

  // Get marketplace contract address for the selected chain
  const marketplaceContract = MARKETPLACE_CONTRACTS.find(contract => contract.chain.id === chainId);

  // Fetch all listings and auctions to match with whitelisted collections
  const {
    listings,
    loading: isLoadingListings
  } = useDirectListings({
    chainId,
    contractAddress: marketplaceContract?.address as Address
  });
  const {
    auctions,
    loading: isLoadingAuctions
  } = useEnglishAuctions({
    chainId,
    contractAddress: marketplaceContract?.address as Address
  });

  // Sort collections based on selected option
  const sortCollections = (collections_0: CollectionWithActivity[], option: SortOption) => {
    return [...collections_0].sort((a, b) => {
      switch (option) {
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        case 'listings':
          return b.listingsCount - a.listingsCount;
        case 'auctions':
          return b.auctionsCount - a.auctionsCount;
        case 'total':
          return b.listingsCount + b.auctionsCount - (a.listingsCount + a.auctionsCount);
        default:
          return 0;
      }
    });
  };

  // Handle sort change
  const handleSortChange = (value: string) => {
    setSortOption(value as SortOption);
  };
  useEffect(() => {
    const loadCollections = () => {
      if (isLoadingListings || isLoadingAuctions) return;
      setIsLoading(true);
      try {
        // Get whitelisted collections for current chain
        const whitelistedCollections = getMarketplaceCollections(chainId as ChainType);

        // Enhance collections with listings and auctions count
        const enhancedCollections = whitelistedCollections.map(collection => {
          const listingsCount = listings?.filter(listing => listing.assetContract.toLowerCase() === collection.address.toLowerCase()).length || 0;
          const auctionsCount = auctions?.filter(auction => auction.assetContract.toLowerCase() === collection.address.toLowerCase()).length || 0;
          return {
            ...collection,
            listingsCount,
            auctionsCount
          };
        });
        setCollections(enhancedCollections);
      } catch (error) {
        console.error('Error loading collections:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCollections();
  }, [chainId, listings, auctions, isLoadingListings, isLoadingAuctions]);

  // Get sorted collections
  const sortedCollections = sortCollections(collections, sortOption);
  return <div>
      {/* Sort controls */}
      {!isLoading && collections.length > 0 && <div className="flex justify-end mb-6">
          <div>
            <Select selectedKeys={[sortOption]} onSelectionChange={keys => handleSortChange(Array.from(keys)[0] as string)} aria-label="Sort collections" placeholder="Select sort option" className="w-40">
              <SelectItem key="alphabetical" startContent={<SortAlphaIcon />}>
                A-Z
              </SelectItem>
              <SelectItem key="listings" startContent={<ListingsIcon />}>
                Listings
              </SelectItem>
              <SelectItem key="auctions" startContent={<AuctionsIcon />}>
                Auctions
              </SelectItem>
              <SelectItem key="total" startContent={<TotalActivityIcon />}>
                Activity
              </SelectItem>
            </Select>
          </div>
        </div>}

      {isLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <Card key={i} className="overflow-hidden">
              <div className="h-40 bg-gray-800">
                <Skeleton className="h-40 w-full" />
              </div>
              <div className="p-6">
                <Skeleton className="h-8 w-3/4 mb-3" />
                <Skeleton className="h-6 w-1/2 mb-2" />
                <Skeleton className="h-5 w-3/4" />
              </div>
            </Card>)}
        </div> : collections.length === 0 ? <div className="text-center py-12">
          <h3 className="text-xl font-semibold mb-4">No Collections Found</h3>
          <p className="text-neutral-400 mb-6">
            There are currently no NFT collections with active listings or auctions.
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCollections.map(collection_0 => <Card key={collection_0.address} className="overflow-hidden hover:shadow-md transition-all">
              <Link href={getCanonicalCollectionUrl(chainId, collection_0)} className="block">
                {/* Collection Banner */}
                <div className="relative h-40 w-full overflow-hidden bg-gray-800">
                  {collection_0.banner ? <Image src={collection_0.banner} alt={`${collection_0.name} banner`} fill className="object-cover transition-transform hover:scale-105" onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = '/placeholder.png';
            }} /> : collection_0.thumbnail ? <Image src={collection_0.thumbnail} alt={`${collection_0.name} thumbnail`} fill className="object-cover transition-transform hover:scale-105" onError={e_0 => {
              const target_0 = e_0.target as HTMLImageElement;
              target_0.src = '/placeholder.png';
            }} /> : <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <span className="text-4xl font-bold text-gray-600">{collection_0.symbol || collection_0.name.substring(0, 2)}</span>
                    </div>}
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl font-semibold truncate hover:text-primary transition-colors mr-2">
                      {collection_0.name}
                    </h3>
                    {collection_0.symbol && <Badge className="bg-gray-800">{collection_0.symbol}</Badge>}
                  </div>

                  {collection_0.description && <p className="text-neutral-400 mb-4 line-clamp-2 text-sm">
                      {collection_0.description}
                    </p>}

                  <div className="flex flex-wrap gap-3 mt-4">
                    {collection_0.listingsCount > 0 && <Badge color="primary" variant="flat" className="text-xs">
                        {collection_0.listingsCount} Listing{collection_0.listingsCount !== 1 ? 's' : ''}
                      </Badge>}
                    {collection_0.auctionsCount > 0 && <Badge color="secondary" variant="flat" className="text-xs">
                        {collection_0.auctionsCount} Auction{collection_0.auctionsCount !== 1 ? 's' : ''}
                      </Badge>}
                    {collection_0.maxSupply && <Badge color="default" variant="flat" className="text-xs">
                        <Format.Number value={collection_0.maxSupply} /> Items
                      </Badge>}
                    {collection_0.mintActive && <Badge color="success" variant="flat" className="text-xs">
                        Mint Active
                      </Badge>}
                  </div>
                </div>
              </Link>
            </Card>)}
        </div>}
    </div>;
}